import React, { ChangeEvent, FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useHistory } from 'react-router';

import { Box, Button, Checkbox, FormControlLabel, FormGroup, Grid, Stack, TextField, Typography } from '@mui/material';

import DateTimeWidget from '~/components/Common/Elements/DateTimeWidget';
import ImageUploadField from '~/components/Common/Forms/ImageUploadField';
import MarkdownEditor from '~/components/Common/Forms/widgets/MarkdownEditor';
import Spinner from '~/components/Common/Spinner';
import InputCruiseLineSelect from '~/components/Cruises/components/Selects/InputCruiseLineSelect';
import InputDepartureSelect from '~/components/Cruises/components/Selects/InputDepartureSelect';
import InputRateCodeSelect from '~/components/Cruises/components/Selects/InputRateCodeSelect';
import { BRANDS } from '~/components/Cruises/constants';

import { newDate } from '~/services/TimeService';
import { CruiseLine } from '~/services/cruises/CruiseLineService';
import { CruiseDealCrawler, CruiseDealSailingCrawler } from '~/services/cruises/DealCrawlerService';

import cruiseDealStatus from '~/utils/cruiseDealStatus';
import { noop } from '~/utils/noop';
import { getSlug } from '~/utils/slugUtils';

import DealInclusionList from '../DealPageEdit/DealInclusion/DealInclusionList';
import { INITIAL_FORM_STATE } from '../constants';
import { CruiseDealPage, CruiseDealPageCreation, LeBrand } from '../types';

type Props = {
  dealPage?: CruiseDealPage;
  dealCrawler?: CruiseDealCrawler;
  crawlerDepartures?: Array<CruiseDealSailingCrawler>;
  isFetching?: boolean;
  children?: ReactNode;
  onSubmit: (formValues: CruiseDealPageCreation) => void;
};

function sanitizeAndTruncate(value?: string, maxLength = 60) {
  return value.replace(/\s+/g, ' ').replace(/[‘’]/g, "'").replace(/[“”]/g, '"').trim().slice(0, maxLength);
}

function getInitialFormValues(dealCrawler?: CruiseDealCrawler) {
  if (!dealCrawler) {
    return INITIAL_FORM_STATE;
  }

  return {
    ...INITIAL_FORM_STATE,
    name: sanitizeAndTruncate(dealCrawler?.dealPageName) || '',
    heading: sanitizeAndTruncate(dealCrawler?.dealPageName) || '',
    subHeading: sanitizeAndTruncate(dealCrawler?.dealPageDescription, 150) || '',
    slug: getSlug(dealCrawler?.dealPageName || ''),
    cruiseLine: {
      id: dealCrawler?.vendor?.id || '',
      name: dealCrawler?.vendor?.name || '',
      code: dealCrawler?.vendor?.code || '',
      whiteImageId: null,
    },
    endDate: dealCrawler?.dueDate || '',
  };
}

function getInitialDepartureIds(dealPage?: CruiseDealPage, crawlerDepartures?: Array<CruiseDealSailingCrawler>) {
  return (
    dealPage?.departures.map((departure) => departure.id) ||
    crawlerDepartures?.map((departure) => departure.departure.id) ||
    []
  );
}

function DealPageForm({ dealPage, dealCrawler, crawlerDepartures, isFetching, children, onSubmit = noop }: Props) {
  const history = useHistory();

  const [formValues, setFormValues] = useState<CruiseDealPage>(getInitialFormValues(dealCrawler));
  const [countRefreshMarkdown, setCountRefreshMarkdown] = useState(0);
  const [previousCruiseLineId, setPreviousCruiseLineId] = useState<string>(dealPage?.cruiseLine.id || '');
  const [selectedRateIds, setSelectedRateIds] = useState<Array<string>>(dealPage?.rates.map((rate) => rate.id) || []);
  const [selectedRateCodes, setSelectedRateCodes] = useState<Array<string>>(
    dealPage?.rates.map((rate) => rate.code) || [],
  );
  const [selectedDepartureIds, setSelectedDepartureIds] = useState<Array<string>>(
    getInitialDepartureIds(dealPage, crawlerDepartures),
  );

  useEffect(() => {
    if (dealPage) {
      setFormValues(dealPage);
    }
  }, [dealPage]);

  // Used to force a refresh of the Markdown editors
  useEffect(() => {
    setCountRefreshMarkdown(countRefreshMarkdown + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dealStatus = useMemo(() => {
    const { startDate, endDate } = formValues;
    if (!startDate || !endDate) {
      return;
    }

    return cruiseDealStatus({ startDate, endDate });
  }, [formValues]);

  const isFormValid = useMemo(() => {
    return (
      formValues.name &&
      formValues.heading &&
      formValues.subHeading &&
      formValues.termsAndConditions &&
      formValues.startDate &&
      formValues.endDate &&
      formValues.cruiseLine?.id &&
      formValues.slug &&
      (selectedRateIds.length || selectedDepartureIds.length)
    );
  }, [formValues, selectedRateIds, selectedDepartureIds]);

  const handleChangeField = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.persist();

      if (event?.target?.name) {
        setFormValues({
          ...formValues,
          [event.target.name]: event.target.value ?? undefined,
        });
      }
    },
    [formValues],
  );

  const handleSlugNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    event.persist();

    if (event?.target?.name) {
      setFormValues((prev) => ({ ...prev, slug: getSlug(event.target.value) }));
    }
  }, []);

  const handleChangeMarkdown = useCallback((value: string) => {
    setFormValues((prev) => ({ ...prev, termsAndConditions: value }));
  }, []);

  const clearRates = useCallback(() => {
    setSelectedRateIds([]);
    setSelectedRateCodes([]);
  }, []);

  const handleCruiseLineField = useCallback(
    (cruiseLine: CruiseLine) => {
      if (previousCruiseLineId && previousCruiseLineId !== cruiseLine?.id) {
        clearRates();
      }

      setPreviousCruiseLineId(cruiseLine?.id ?? '');
      setFormValues({
        ...formValues,
        cruiseLine: {
          id: cruiseLine?.id ?? '',
          name: cruiseLine?.name ?? '',
          code: cruiseLine?.code ?? '',
          whiteImageId: null,
        },
      });
    },
    [formValues, previousCruiseLineId, clearRates],
  );

  const handleAddImage = useCallback((_: string, imageId: string) => {
    setFormValues((prev) => ({ ...prev, imageId }));
    // Return a promise because the ImageUploadField expects a promise
    return Promise.resolve();
  }, []);

  const handleSelectBrand = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    event.persist();

    if (event?.target?.name) {
      setFormValues((prev) => ({
        ...prev,
        availableInBrands: [...prev.availableInBrands, event.target.name as LeBrand],
      }));
    }
  }, []);

  const handleCancel = useCallback(() => {
    history.push(`/cruises/deals`);
  }, [history]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onSubmit({
        name: formValues.name,
        heading: formValues.heading,
        subHeading: formValues.subHeading,
        termsAndConditions: formValues.termsAndConditions,
        startDate: formValues.startDate,
        endDate: formValues.endDate,
        cruiseLineId: formValues.cruiseLine.id,
        slug: formValues.slug,
        imageId: formValues.imageId,
        departureIds: selectedDepartureIds,
        rateIds: selectedRateIds,
        availableInBrands: formValues.availableInBrands,
      });
    },
    [
      formValues.cruiseLine.id,
      formValues.endDate,
      formValues.heading,
      formValues.imageId,
      formValues.name,
      formValues.slug,
      formValues.startDate,
      formValues.subHeading,
      formValues.termsAndConditions,
      formValues.availableInBrands,
      onSubmit,
      selectedRateIds,
      selectedDepartureIds,
    ],
  );

  const initialDepartures = useMemo(() => {
    if (dealPage) {
      return dealPage.departures.map((departure) => ({
        id: departure.id,
        externalId: String(departure.externalId),
        name: departure.name,
      }));
    }

    return crawlerDepartures?.map(({ departure }) => ({
      id: departure.id,
      externalId: String(departure.externalId),
      name: departure.name,
    }));
  }, [crawlerDepartures, dealPage]);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            required
            fullWidth
            name="name"
            label="Deal Page Name"
            inputProps={{ maxLength: 60 }}
            helperText="The maximum length is 60 characters."
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              handleChangeField(event);
              handleSlugNameChange(event);
            }}
            value={formValues.name}
          />
        </Grid>

        <Grid item xs={4}>
          <InputCruiseLineSelect
            label="Cruise line"
            value={formValues.cruiseLine.id}
            onChange={handleCruiseLineField}
            required
            withApprovedOffers
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            required
            fullWidth
            name="slug"
            label="Deal Page Slug"
            onChange={handleChangeField}
            value={formValues.slug}
          />
        </Grid>

        <Grid item xs={4}>
          <TextField
            required
            fullWidth
            name="heading"
            label="Headline"
            onChange={handleChangeField}
            inputProps={{ maxLength: 60 }}
            value={formValues.heading}
            helperText="The maximum length is 60 characters."
          />
        </Grid>
        <Grid item xs={8}>
          <TextField
            required
            fullWidth
            name="subHeading"
            label="Sub-headline"
            onChange={handleChangeField}
            inputProps={{ maxLength: 150 }}
            value={formValues.subHeading}
            helperText="The maximum length is 150 characters."
          />
        </Grid>

        <Grid item container xs={12} spacing={2} marginTop={2}>
          <Grid item xs={2} alignSelf="center">
            <Typography>Schedule [AEST]</Typography>
          </Grid>
          <Grid item xs={3}>
            <DateTimeWidget
              required
              defaultTimezone="Australia/Sydney"
              value={formValues.startDate}
              onChange={(date) =>
                setFormValues((prev) => ({
                  ...prev,
                  startDate: date.length ? date : '',
                }))
              }
              format="DD/MM/YYYY HH:mm"
              label="Start Date"
            />
          </Grid>
          <Grid item xs={3}>
            <DateTimeWidget
              required
              defaultTimezone="Australia/Sydney"
              value={formValues.endDate}
              onChange={(date) =>
                setFormValues((prev) => ({
                  ...prev,
                  endDate: date.length ? date : '',
                }))
              }
              format="DD/MM/YYYY HH:mm"
              minDate={formValues.startDate ? newDate(formValues.startDate) : undefined}
              label="End Date"
            />
          </Grid>
          <Grid item xs={3} alignSelf="center">
            <Typography color={dealStatus?.color}>{dealStatus?.status}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography>Terms & Conditions*</Typography>
          <MarkdownEditor
            required
            // Used to force a refresh of the Markdown editors
            key={`termsAndConditions_${countRefreshMarkdown}`}
            value={formValues.termsAndConditions}
            onChange={handleChangeMarkdown}
          />
        </Grid>

        <Grid item xs={8}>
          <FormGroup>
            <Typography fontWeight="bold">Available In Brands:</Typography>

            <Stack direction="row">
              {BRANDS.map((brand) => (
                <FormControlLabel
                  key={brand}
                  control={<Checkbox defaultChecked />}
                  label={brand}
                  name={brand}
                  checked={formValues.availableInBrands.includes(brand as LeBrand)}
                  onChange={handleSelectBrand}
                />
              ))}
            </Stack>
          </FormGroup>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <ImageUploadField
              col={12}
              label="Hero image"
              field_key="imageId"
              value={formValues.imageId}
              multiple={false}
              onUpload={handleAddImage}
              imageFullWidth
              imageFitType="contain"
            />
          </Box>
        </Grid>

        {!selectedDepartureIds.length && (
          <Grid item xs={12} marginTop={2}>
            <InputRateCodeSelect
              vendorCode={formValues.cruiseLine?.code}
              selectedRateIds={selectedRateIds}
              selectedRateCodes={selectedRateCodes}
              isLoading={!formValues.cruiseLine}
              onChange={setSelectedRateIds}
            />
          </Grid>
        )}

        {!selectedRateIds.length && (
          <Grid item xs={12} marginTop={2}>
            <InputDepartureSelect
              initialDepartures={initialDepartures}
              selectedDepartureIds={selectedDepartureIds}
              onChange={setSelectedDepartureIds}
              vendorId={formValues.cruiseLine?.id}
            />
          </Grid>
        )}

        {!!dealPage && (
          <Grid item xs={12} marginTop={2}>
            <DealInclusionList />
          </Grid>
        )}

        {!dealPage && !dealCrawler && (
          <Grid item xs={12} marginTop={2}>
            <Typography>You will be able to add inclusions after saving the deal page.</Typography>
          </Grid>
        )}
      </Grid>

      {children}

      <Box mt={3} gap={1} display="flex" justifyContent="end">
        <Button variant="outlined" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" type="submit" disabled={!!isFetching || !isFormValid}>
          {!!isFetching && <Spinner size={20} />}
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}

export default DealPageForm;
